import { ReactNode } from 'react';

export type CalloutTone = 'info' | 'success' | 'warn' | 'danger' | 'neutral';

interface CalloutProps {
  tone?: CalloutTone;
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
}

const toneClasses: Record<CalloutTone, { wrap: string; accent: string; body: string }> = {
  info:    { wrap: 'bg-accent-soft  border-accent/20',  accent: 'text-accent',  body: 'text-text-2' },
  success: { wrap: 'bg-success-soft border-success/20', accent: 'text-success', body: 'text-text-2' },
  warn:    { wrap: 'bg-warn-soft    border-warn/20',    accent: 'text-warn',    body: 'text-text-2' },
  danger:  { wrap: 'bg-danger-soft  border-danger/20',  accent: 'text-danger',  body: 'text-text-2' },
  neutral: { wrap: 'bg-bg-elevated  border-border',     accent: 'text-text-2',  body: 'text-text-3' },
};

const defaultIcons: Record<CalloutTone, string> = {
  info: 'ⓘ',
  success: '✓',
  warn: '⚠',
  danger: '⚠',
  neutral: '·',
};

/** Themed inline banner — replaces antd <Alert> for our palette. */
export function Callout({ tone = 'info', title, children, icon }: CalloutProps) {
  const t = toneClasses[tone];
  return (
    <div className={`flex items-start gap-2.5 rounded-md border px-3 py-2 ${t.wrap}`}>
      <span className={`text-[13px] leading-[18px] font-semibold flex-shrink-0 ${t.accent}`}>
        {icon ?? defaultIcons[tone]}
      </span>
      <div className="min-w-0 flex-1">
        {title && (
          <div className={`text-[12.5px] font-semibold leading-[18px] ${t.accent}`}>{title}</div>
        )}
        {children && (
          <div className={`text-[12.5px] leading-[18px] ${title ? 'mt-0.5' : ''} ${t.body}`}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
